import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { useShuffleArenaOfferMutation } from '@/api/queries/shop';
import { ticketImages } from '@/assets/img/lottery';
import { playSound } from '@/common/lib/soundEffect';
import {
  type ArenaOffersDto,
  type ArenaPackRewardDto,
  type BoxReward,
} from '@/common/types/shop/marketBoxes';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import { type ProfitDropRewardType } from '@/components/OffersMarket/ArenaOffers/config';
import RewardArray from '@/components/OffersMarket/ArenaOffers/RewardArray';
import {
  H3,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';

import { GreyBlurSmButton, MainButton } from '../../Button/Button';
import { Picture } from '../../Picture/Picture';

import blurredLight from '/img/blurred-light.png';
import goldLightPng from '/img/light/gold-light.png';
import goldLightWebp from '/img/light/gold-light.webp';
import cyanLightPng from '/img/light-cyan.png';
import cyanLightWebp from '/img/light-cyan.webp';
import usdt6Png from '/img/shop/arenaOffers/6usdt.png';
import usdt6Webp from '/img/shop/arenaOffers/6usdt.webp';
import usdt15Png from '/img/shop/arenaOffers/15usdt.png';
import usdt15Webp from '/img/shop/arenaOffers/15usdt.webp';
import purpleLightPng from '/img/shop/arenaOffers/light.png';
import purpleLightWebp from '/img/shop/arenaOffers/light.webp';
import arrows from '/img/shop/boxes/arrows.svg';
import tgemBigPng from '/img/tgem-big.png';
import tgemBigWebp from '/img/tgem-big.webp';

interface ClaimStatusRewardProps {
  type: ProfitDropRewardType;
  rewardArray: (ArenaPackRewardDto | BoxReward)[];
  rewardKey: keyof ArenaOffersDto;
  currentRewardIndex: number;
  onNext: () => void;
  isLast?: boolean;
  offer: ArenaOffersDto;
}

const StringMotion = motion.create(StringMain);

const ClaimStatusReward: FC<ClaimStatusRewardProps> = ({
  type,
  rewardArray,
  currentRewardIndex,
  onNext,
  isLast,
  offer,
  rewardKey,
}) => {
  const { t } = useTranslation();
  const [shuffle, { isLoading }] = useShuffleArenaOfferMutation();

  // useEffect(() => {
  //   if (!isLast) return;

  //   const timeoutId = setTimeout(() => {
  //     window.addEventListener('click', onNext);
  //   }, 1000);

  //   return (): void => {
  //     clearTimeout(timeoutId);
  //     window.removeEventListener('click', onNext);
  //   };
  // }, [isLast, onNext]);

  const value = rewardArray[currentRewardIndex];

  const animationProps = {
    animate: { opacity: 1 },
    initial: { opacity: 0 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  } as const;

  return (
    <>
      {isLast && (
        <Confetti
          className="-z-[1] w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          loop={false}
          autoPlay={true}
          speed={2}
          width="100%"
          height="100%"
        />
      )}
      {type === 'lottery' && (
        <motion.div
          {...animationProps}
          className="flex-1 flex items-center justify-center self-stretch"
        >
          <div className="h-[19.375rem] w-full relative z-0 pt-20 flex items-center flex-col">
            <Picture
              className="[&>img]:object-contain absolute size-full top-0 z-[-2]"
              img={goldLightPng}
              webp={goldLightWebp}
              alt="reward light"
            />
            <Picture
              className={cn(
                'absolute z-[-1] size-[17rem] animate-long-spin  top-4 mix-blend-luminosity',
              )}
              img={blurredLight}
              alt="light"
            />

            <Picture
              className="size-36"
              img={ticketImages.gold3d.png}
              webp={ticketImages.gold3d.webp}
              alt="reward"
            />
            <StringMain weight="extrabold" className="!text-[2.5rem]">
              {'lottery' in value && value.lottery
                ? value.lottery.goldTicket
                : 0}
            </StringMain>
            <StringMain weight="medium" className="!text-white/60">
              Gold Ticket
            </StringMain>
          </div>
        </motion.div>
      )}
      {type === 'arenaOffer' && (
        <motion.div
          key={'rewards_' + currentRewardIndex}
          {...animationProps}
          className="mt-20 flex flex-col items-center"
        >
          <RewardArray
            variant="big"
            name={offer.name}
            reward={value}
            classNames="items-center"
            offerId={type === 'arenaOffer' ? offer.id : undefined}
          />
        </motion.div>
      )}
      {type === 'usdt' && 'usdt' in value && (
        <motion.div {...animationProps} className="mt-1.5 w-full relative">
          <Picture
            img={cyanLightPng}
            webp={cyanLightWebp}
            alt="cyan light"
            className="absolute w-full -translate-x-1/2 left-1/2 top-1/2 -translate-y-1/2 z-[-1] max-w-[26.75rem]"
          />
          <Picture
            img={value.usdt === 15 ? usdt15Png : usdt6Png}
            webp={value.usdt === 15 ? usdt15Webp : usdt6Webp}
            alt="usdt reward"
            className="h-[16rem] [&>*]:object-cover my-6 block"
          />
        </motion.div>
      )}
      {type === 'tgem' && 'tgem' in value && (
        <motion.div
          {...animationProps}
          className="mt-1.5 w-full relative flex items-center flex-col"
        >
          <Picture
            img={purpleLightPng}
            webp={purpleLightWebp}
            alt="purple light"
            className="absolute w-full -translate-x-1/2 left-1/2 top-1/2 -translate-y-1/2 z-[-1] max-w-[20rem]"
          />
          <Picture
            img={tgemBigPng}
            webp={tgemBigWebp}
            alt="tgem reward"
            className=" mx-auto block size-[9rem] mt-6"
          />
          <H3
            weight="bold"
            className="bg-[#181A20CC] backdrop-blur rounded-lg py-1 px-2 -mt-6"
          >
            {value.tgem}
          </H3>
        </motion.div>
      )}
      {'arenaOffer' === type && (
        <div className="flex flex-col items-center gap-[24px] mt-[24px]">
          {type === 'arenaOffer' && (
            <StringSmall className="text-center !text-[#AAAAAA]">
              {t('Tap to keep 1 component')}
            </StringSmall>
          )}
          <GreyBlurSmButton
            className="!h-[48px] !w-[96px]"
            text=""
            isLoading={isLoading}
            onClick={() => {
              playSound('buttons/shuffle');
              shuffle({ id: offer.id, body: { [rewardKey]: value } });
            }}
          >
            <img src={arrows} alt="arrows" className="size-[24px]" />
          </GreyBlurSmButton>
        </div>
      )}
      {isLast && type !== 'lottery' ? (
        <StringMotion
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ delay: 0.15, duration: 0.3 }}
          className="absolute bottom-[5.25rem] left-1/2 -translate-x-1/2 w-full text-center !text-[20px]"
          weight="extrabold"
          onClick={onNext}
        >
          {t('Tap to continue')}
        </StringMotion>
      ) : (
        <MainButton
          onClick={onNext}
          text={`${t('Claim')} ${currentRewardIndex + 1}/${rewardArray.length}`}
          className="!w-[12.5rem] mt-10 720-h:mt-6"
        />
      )}
    </>
  );
};

export default ClaimStatusReward;
